import { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import {
  useTracking,
  filterOrdersByTab,
  getOrderCountByTab,
} from "../hooks/useTracking";
import {
  TrackingTabs,
  SearchFilters,
  OrdersList,
  type TabType,
  type TabData,
} from "../components/Tracking";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import { Pagination } from "../components/_ResponsiveComponents";
import SEO from "../components/SEO/SEO";

const Tracking = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("active");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(10);

  // Custom hook for tracking data
  const { orders, loading } = useTracking({
    searchTerm,
  });

  useEffect(() => {
    if (!user) {
      navigate("/registration");
    }
  }, [user, navigate]);

  // Reset page when search term or active tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleLimitChange = (limit: number) => {
    setResultsPerPage(limit);
    setCurrentPage(1); // Reset to first page when changing limit
  };

  // Filter orders based on active tab and search term
  const allFilteredOrders = filterOrdersByTab(orders, activeTab, searchTerm);

  // Calculate pagination for filtered results
  const filteredCount = allFilteredOrders.length;
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const paginatedFilteredOrders = allFilteredOrders.slice(startIndex, endIndex);
  const calculatedTotalPages = Math.ceil(filteredCount / resultsPerPage);

  // Prepare tabs data with counts
  const tabsData: TabData[] = [
    {
      key: "active",
      label: "Active",
      count: getOrderCountByTab(orders, "active"),
    },
    {
      key: "shipped",
      label: "Shipped",
      count: getOrderCountByTab(orders, "shipped"),
    },
    {
      key: "delivered",
      label: "Delivered",
      count: getOrderCountByTab(orders, "delivered"),
    },
    {
      key: "cancelled",
      label: "Cancelled",
      count: getOrderCountByTab(orders, "cancelled"),
    },
  ];

  return (
    <>
      <SEO title="My Orders" description="Track and manage your orders." noindex />
      <Header />
      <div className="min-h-screen bg-section-bg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {/* Simple Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-section-text mb-2">My Orders</h1>
            <p className="text-section-muted">Track and manage your orders</p>
          </div>

          {/* Main Content Card */}
          <div className="bg-section-card-bg rounded-2xl shadow-xl border border-section-border overflow-hidden">
            {/* Tabs Section */}
            <TrackingTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              tabsData={tabsData}
            />

            {/* Search Section */}
            <SearchFilters
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />

            {/* Orders List Section */}
            <OrdersList
              orders={orders}
              filteredOrders={paginatedFilteredOrders}
              loading={loading}
            />

            {/* Pagination Section */}
            {filteredCount > 0 && (
              <div className="px-6 py-4 bg-section-card-bg border-t border-section-border">
                <Pagination
                  type="tracking"
                  currentPage={currentPage}
                  totalPages={calculatedTotalPages}
                  totalResults={filteredCount}
                  resultsPerPage={resultsPerPage}
                  onPageChange={handlePageChange}
                  onLimitChange={handleLimitChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Tracking;
